import React, { useEffect, useState } from "react";
import { FaClipboardList, FaSync } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "../css/pages/AuthorizationDashboard.css";
import "../css/standard-components.css";
import { usePageTitle } from "../hooks/usePageTitle";
import {
  AuthorizationDashboardData,
  getAuthorizationDashboard,
} from "../services/authorizationDashboardService";
import { formatTotalAmount } from "../utils/formatAmount";

const AuthorizationDashboard: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();

  const [data, setData] = useState<AuthorizationDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      const result = await getAuthorizationDashboard();
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="page-container" style={{ background: "#fff" }}>
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
          <FaSync style={{ fontSize: "32px", animation: "spin 1s linear infinite", marginBottom: "16px" }} />
          <p>Loading authorization data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container" style={{ background: "#fff" }}>
        <div style={{ textAlign: "center", padding: "60px 20px", color: "#ef4444" }}>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={fetchData} style={{ marginTop: "12px" }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { bookingPending, bookingTotals, incomePending, incomeTotals } = data;

  return (
    <div className="page-container" style={{ background: "#fff" }}>
      <div className="auth-dashboard-header">
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <FaClipboardList style={{ fontSize: "24px", color: "#4f46e5" }} />
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 700, color: "#1f2937" }}>
              Authorization Dashboard
            </h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
              Overview of pending authorizations across all projects
            </p>
          </div>
        </div>
        <button className="btn btn-outline" onClick={fetchData} style={{ padding: "8px 16px", fontSize: "13px" }}>
          <FaSync style={{ marginRight: "6px" }} /> Refresh
        </button>
      </div>

      {/* Pending Booking Authorisation */}
      <div className="auth-dashboard-section">
        <h3 className="auth-section-title" style={{ background: "#4f46e5" }}>
          Pending Booking Authorisation
        </h3>
        <table className="standard-table auth-dashboard-table">
          <thead>
            <tr>
              <th style={{ width: "50px", textAlign: "center" }}>Sr</th>
              <th>Site Name</th>
              <th style={{ textAlign: "center" }}>Fresh Booking</th>
              <th style={{ textAlign: "center" }}>Edits</th>
              <th style={{ textAlign: "center" }}>Transfer</th>
            </tr>
          </thead>
          <tbody>
            {bookingPending.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                  No pending booking authorizations
                </td>
              </tr>
            ) : (
              <>
                {bookingPending.map((row, index) => (
                  <tr key={row.projectId}>
                    <td style={{ textAlign: "center", fontWeight: 600, color: "#4f46e5" }}>{index + 1}</td>
                    <td style={{ fontWeight: 500 }}>{row.projectName}</td>
                    <td
                      className={`auth-count-cell ${row.freshBooking > 0 ? "has-pending" : ""}`}
                      onClick={() => row.freshBooking > 0 && navigate("/approval/booking")}
                    >
                      {row.freshBooking}
                    </td>
                    <td
                      className={`auth-count-cell ${row.edits > 0 ? "has-pending" : ""}`}
                      onClick={() => row.edits > 0 && navigate("/approval/booking")}
                    >
                      {row.edits}
                    </td>
                    <td
                      className={`auth-count-cell ${row.transfer > 0 ? "has-pending" : ""}`}
                      onClick={() => row.transfer > 0 && navigate("/approval/booking")}
                    >
                      {row.transfer}
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="auth-totals-row">
                  <td></td>
                  <td style={{ fontWeight: 700 }}>Total</td>
                  <td className="auth-count-cell total">{bookingTotals.freshBooking}</td>
                  <td className="auth-count-cell total">{bookingTotals.edits}</td>
                  <td className="auth-count-cell total">{bookingTotals.transfer}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pending Income Authorisation */}
      <div className="auth-dashboard-section">
        <h3 className="auth-section-title" style={{ background: "#059669" }}>
          Pending Income Authorisation
        </h3>
        <table className="standard-table auth-dashboard-table">
          <thead>
            <tr>
              <th style={{ width: "50px", textAlign: "center" }}>Sr</th>
              <th>Site Name</th>
              <th style={{ textAlign: "center" }}>Income</th>
              <th style={{ textAlign: "right" }}>Income TOTAL</th>
              <th className="auth-hidden-header" style={{ textAlign: "center" }}>Receivable Income</th>
              <th className="auth-hidden-header" style={{ textAlign: "right" }}>Receivable Income TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {incomePending.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: "24px", color: "#9ca3af" }}>
                  No pending income authorizations
                </td>
              </tr>
            ) : (
              <>
                {incomePending.map((row, index) => (
                  <tr key={row.projectId}>
                    <td style={{ textAlign: "center", fontWeight: 600, color: "#4f46e5" }}>{index + 1}</td>
                    <td style={{ fontWeight: 500 }}>{row.projectName}</td>
                    <td
                      className={`auth-count-cell ${row.incomeCount > 0 ? "has-pending" : ""}`}
                      onClick={() => row.incomeCount > 0 && navigate("/approval/income")}
                    >
                      {row.incomeCount}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "#059669" }}>
                      {formatTotalAmount(row.incomeTotal)}
                    </td>
                    <td
                      className={`auth-count-cell ${row.hiddenIncomeCount > 0 ? "has-pending-red" : ""}`}
                      onClick={() => row.hiddenIncomeCount > 0 && navigate("/approval/hidden-income")}
                    >
                      {row.hiddenIncomeCount}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: 600, color: "#dc2626" }}>
                      {formatTotalAmount(row.hiddenIncomeTotal)}
                    </td>
                  </tr>
                ))}
                {/* Totals Row */}
                <tr className="auth-totals-row">
                  <td></td>
                  <td style={{ fontWeight: 700 }}>Total</td>
                  <td className="auth-count-cell total">{incomeTotals.incomeCount}</td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "#059669", fontSize: "15px" }}>
                    {formatTotalAmount(incomeTotals.incomeTotal)}
                  </td>
                  <td className="auth-count-cell total" style={{ color: "#dc2626" }}>
                    {incomeTotals.hiddenIncomeCount}
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700, color: "#dc2626", fontSize: "15px" }}>
                    {formatTotalAmount(incomeTotals.hiddenIncomeTotal)}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuthorizationDashboard;
